import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { getPagePath, trans, useSoyuzLocales } from '@spotahome/soyuz/client';

import classNames from 'classnames';

import Crumb from './components/Crumb';
import CrumbSeparator from './components/CrumbSeparator';

import { getNewCountryUrl } from './getCountryUrl';

import styles from './SearchBreadcrumbs.module.scss';

// Must stay in sync with server/bff/src/server/routes/pages/marketplace/data/properties.json.
// Other values (e.g. "rentals") are silently dropped by path-to-regexp and would
// collapse the property-type crumb URL onto the city crumb URL.
const ROUTE_PROPERTY_TYPES = ['apartments', 'residences', 'rooms', 'studios'];

const crumbsBuilder = ({
  cityId,
  cityName,
  countryId,
  propertyType,
  topology,
  anchorText = null,
  locale
}) => {
  const type = propertyType[0];

  const countryUrl = getNewCountryUrl(countryId, locale);
  const hasPropertyType =
    propertyType?.length === 1 &&
    type &&
    cityId &&
    ROUTE_PROPERTY_TYPES.includes(type);
  const hasTopology = topology?.length === 1 && topology[0];

  const crumbs = [
    {
      label: trans('breadcrumb.label.home'),
      href: getPagePath('landing')
    },
    countryUrl
      ? {
          label: trans(`country.${countryId}`),
          href: countryUrl
        }
      : null,
    cityId
      ? {
          label: cityName,
          href: getPagePath('marketplace.free-search', {
            params: { locationString: cityId }
          })
        }
      : null,
    hasPropertyType
      ? {
          label: trans(`breadcrumb.label.${type}_for_rent`),
          href: getPagePath('marketplace.free-search', {
            params: {
              locationString: cityId,
              [`rent_${type}`]: `for-rent:${type}`
            }
          })
          // href: `${cityHref}/for-rent:${type}`
        }
      : null,
    hasPropertyType && hasTopology
      ? {
          label: trans(`search.others.apartments-${topology[0]}`),
          href: getPagePath('marketplace.free-search', {
            params: {
              locationString: cityId,
              [`rent_${type}`]: `for-rent:${type}`,
              [`bedrooms_${topology[0]}`]: `bedrooms:${topology[0]}`
            }
          })
        }
      : null,
    anchorText
      ? {
          label: anchorText,
          href: ''
        }
      : null
  ];

  return crumbs.filter(Boolean);
};

const SearchBreadcrumbs = ({
  cityId = null,
  cityName = null,
  countryId,
  propertyType = [],
  topology = [],
  anchorText = null,
  className = ''
}) => {
  const { current: locale } = useSoyuzLocales();

  const crumbs = crumbsBuilder({
    cityId,
    cityName,
    countryId,
    propertyType,
    topology,
    anchorText,
    locale
  });

  return (
    <div
      className={classNames(styles['search-breadcrumbs'], className)}
      itemScope
      itemType="https://schema.org/BreadcrumbList"
    >
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <Fragment key={crumb.label}>
            <Crumb
              label={crumb.label}
              href={isLast ? null : crumb.href}
              position={i + 1}
            />
            {!isLast && <CrumbSeparator />}
          </Fragment>
        );
      })}
    </div>
  );
};

SearchBreadcrumbs.propTypes = {
  countryId: PropTypes.string.isRequired,
  cityId: PropTypes.string,
  cityName: PropTypes.string,
  propertyType: PropTypes.arrayOf(PropTypes.string),
  topology: PropTypes.arrayOf(PropTypes.string),
  anchorText: PropTypes.string,
  className: PropTypes.string
};

export default SearchBreadcrumbs;
